<template>
  <div v-if="platformPermissionsLoaded && checkPermission('insights.crossover.audience')">
    <SmartFilters ref="smartFilters" :parent="'core_audience_insights'"/>
    
    <CRow>
      <CCol col="12" xl="12">
        <CCard class="mb-0">
          <CCardHeader>
            <CRow>
              <CCol cols="4" xl="4" class="pt-0 pb-0 text-left">
                {{ $t('insights.Overview_activations') }}
              </CCol>
              <CCol cols="8" xl="8" class="pt-0 pb-0 text-xl-right">
                <b-switch class="mb-0 mt-1 mt-xl-0 enable_search" v-model="searchEnabled" size="is-small">{{ $t('common.activate_search') }}</b-switch>
                <div class="mt-1 mt-xl-0 d-inline-block align-top">
                  <CButton class="ml-2 link" color="primary" @click="exportData('csv')">
                    <i class="fas fa-file-arrow-down mr-1"/><span>{{ $t('common.Export_as_csv') }}</span>
                  </CButton>                
                  <CButton class="ml-2 link" color="primary" @click="exportData('json');">
                    <i class="fas fa-file-arrow-down mr-1"/><span>{{ $t('common.Export_as_json') }}</span>              
                  </CButton>
                </div>              
              </CCol>  
            </CRow>
          </CCardHeader>
          <CCardBody class="p-0">
            <CRow>
              <CCol cols="12" lg="12" :class="{'pt-0 pb-0' : !loadingInsightsData}">
                <div v-if="loadingInsightsData" class="h-100 d-flex align-items-center">
                  <loadingSpinner mode="auto" :content="null"/>
                </div>
                <b-table v-else
                         class="data_table" 
                         :data="audienceLogins" 
                         :striped="true" 
                         :hoverable="true" 
                         :mobile-cards="true" 
                         :paginated="isPaginated" 
                         :per-page="perPage" 
                         :current-page.sync="currentPage" 
                         :pagination-simple="isPaginationSimple" 
                         :pagination-position="paginationPosition" 
                         :default-sort-direction="defaultSortDirection" 
                         :sort-icon="sortIcon" 
                         :sort-icon-size="sortIconSize" 
                         default-sort="Department/Location">
                  
                  <template slot-scope="props">
                    <b-table-column field="department" :label="$t('common.Department')" :searchable="searchEnabled" :sortable="true">
                      <span>{{props.row.department}}</span>
                    </b-table-column>
                    <b-table-column field="team" :label="$t('common.Team')" :searchable="searchEnabled" :sortable="true">
                      <span>{{props.row.team !== null ? props.row.team : '-' }}</span>
                    </b-table-column>                    
                    <b-table-column field="total_users" :label="$t('common.Total_users')" :searchable="false" :sortable="true" width="10%">
                      <span>{{props.row.total_users}}</span>
                    </b-table-column>
                    <b-table-column field="logged_in_amount" :label="$t('insights.Users_activated')" :searchable="false" :sortable="true" width="10%">
                      <span>{{props.row.logged_in_amount}}</span>
                    </b-table-column>
                    <b-table-column field="percentage" :label="$t('common.Participation')" :searchable="false" :sortable="true" width="10%">
                      <span>{{props.row.percentage}}%</span>
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <div class="p-2 text-center">
                      <span>{{ $t('insights.Statistics_not_found') }}</span>
                    </div>                
                  </template>                
                </b-table>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
  <noPermission v-else-if="platformPermissionsLoaded" trigger="permission"/>
</template>

<script>
import axios from 'axios'
import loadingSpinner from '@/components/common/loadingSpinner.vue'
import noPermission from '@/components/common/noPermission.vue';

import SmartFilters from '@/components/insights/common/SmartFilters.vue';

export default {
  name: 'InsightsAudience',
  components: {
    loadingSpinner,
    noPermission,
    SmartFilters
  },
  data: () => {
    return {
      platformPermissions: [],
      platformPermissionsLoaded: false,      
      audienceLogins: [],
      overviewDataLoaded: false,
      isPaginated: true,
      isPaginationSimple: false,
      paginationPosition: 'bottom',
      currentPage: 1,
      perPage: 15,
      defaultSortDirection: 'asc',
      sortIcon: 'arrow-up',
      sortIconSize: 'is-small',
      showInactive: false,
      searchEnabled: false,
      loadingInsightsData: false,
      smartFilters: []
    }
  },
  methods: {
    getAudienceLogins () {
      let params = {};
      params.filters = this.smartFilters;
      // Start the loader
      this.loadingInsightsData = true;
      // Get the audience insights data      
      axios.post(process.env.VUE_APP_API_URL + '/v1/insights/audience/logins', params)
      .then(res => {
        this.audienceLogins = res.data.data;
        // Check if the pagination should be activated
        (this.audienceLogins.length > this.perPage) ? this.isPaginated = true : this.isPaginated = false;         
        // Hide the loader
        this.loadingInsightsData = false;
      })
      .catch(err => {
        console.error(err); 
      });
    },
    exportData(outputFormat){
      let params = {};
      params.filters = this.smartFilters;

      axios.post(process.env.VUE_APP_API_URL + '/v1/insights/audience/logins/export/' + outputFormat, params)
      .then(res => {
        this.$buefy.toast.open({ message: this.$t('common.Export_successful'), type: 'is-success', duration: 2000 });
        if (!window.navigator.msSaveOrOpenBlob){
          // Blob navigator
          let url = null;
          let outputData = null;
          // Set the output data and URL based on the output format
          if(outputFormat === 'csv') {
            outputData = res.data;
            url = window.URL.createObjectURL(new Blob([outputData]));  
          } else if(outputFormat === 'json') {
            outputData = JSON.stringify(res.data.data);
            url = window.URL.createObjectURL(new Blob([outputData]));
          }
          // Create a new 'a' element
          const link = document.createElement('a');
          // Set the generated URL as href of the new element
          link.href = url;
          // Set the download attribute based on the output format
          if(outputFormat === 'csv') {
            link.setAttribute('download', 'insights-audience-login.csv');
          } else if(outputFormat === 'json') {
            link.setAttribute('download', 'insights-audience-login.json');
          }
          // Append the link to the body
          document.body.appendChild(link);
          // Click the link
          link.click();
        } else {
          // Blob for Explorer 11
          let url = null;
          let outputData = null;
          // Set the output data and URL based on the output format
          if(outputFormat === 'csv') {
            outputData = res.data;
            url = window.navigator.msSaveOrOpenBlob(new Blob([outputData]),"insights-audience-login.csv");
          } else if(outputFormat === 'json') {
            outputData = JSON.stringify(res.data.data);
            url = window.navigator.msSaveOrOpenBlob(new Blob([outputData]),"insights-audience-login.json");
          }
        }
      })
      .catch(err => {
        console.error(err); 
      });      
    },
    getPlatformPermissions() {
      axios.get(process.env.VUE_APP_API_URL + '/v1/core/platform/permissions')
      .then(res => {      
        this.platformPermissions = res.data.data;
        // Update the platformPermissionsLoaded value
        this.platformPermissionsLoaded = true;
      })
      .catch(err => {
        console.error(err); 
      });
    },
    checkPermission(permissionTag) {
      if(this.platformPermissions.includes(permissionTag)) {
        return true;
      } else{
        return false;
      }
    }    
  },
  mounted: function() {
    this.getPlatformPermissions();
    this.getAudienceLogins();

    this.$bus.$on('core_audience_insights_smart_filters_updated', (filters) => {
      this.smartFilters = filters;
      this.getAudienceLogins();
    });    
  },
  beforeDestroy() {
    this.$bus.$off('core_audience_insights_smart_filters_updated');
  }  
}
</script>
